<template>
    <div class="scheduled-delivery-page">
        <div class="page-container">
            <div class="settings-form">
                <!-- 页面标题和统计 -->
                <div class="header-section">
                    <h2 class="page-title">定时投放管理</h2>
                    <div class="stats-section">
                        <el-row :gutter="20">
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon total">
                                        <el-icon>
                                            <Document />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.total_tasks || 0 }}</div>
                                        <div class="stat-label">总任务数</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon active">
                                        <el-icon>
                                            <CircleCheck />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.active_tasks || 0 }}</div>
                                        <div class="stat-label">运行中</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon success">
                                        <el-icon>
                                            <TrendCharts />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.total_deliveries || 0 }}</div>
                                        <div class="stat-label">总投放次数</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon today">
                                        <el-icon>
                                            <Clock />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.today_deliveries || 0 }}</div>
                                        <div class="stat-label">今日投放</div>
                                    </div>
                                </div>
                            </el-col>
                        </el-row>
                    </div>
                </div>

                <!-- 操作按钮区域 -->
                <div class="action-bar">
                    <el-button type="primary" size="large" @click="showCreateDialog = true">
                        <el-icon>
                            <Plus />
                        </el-icon>
                        新建定时任务
                    </el-button>
                    <el-button type="success" size="large" @click="fetchTaskList">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        刷新列表
                    </el-button>
                    <el-button type="info" size="large" @click="showLogsDialog = true">
                        <el-icon>
                            <Document />
                        </el-icon>
                        查看投放日志
                    </el-button>
                </div>

                <!-- 任务列表 -->
                <div class="task-list-section">
                    <el-table :data="taskList" stripe v-loading="loading" style="width: 100%">
                        <el-table-column prop="task_name" label="任务名称" min-width="150" show-overflow-tooltip />
                        <el-table-column prop="ghz_name" label="广告账户" width="120" />
                        <el-table-column prop="corporation_name" label="广告主" width="150" show-overflow-tooltip />

                        <el-table-column label="投放目标" width="200" show-overflow-tooltip>
                            <template #default="{ row }">
                                <div class="target-info">
                                    <div v-if="row.adgroup_name" class="ad-name">
                                        <el-tag size="small">广告</el-tag>
                                        {{ row.adgroup_name }}
                                    </div>
                                    <div v-if="row.dynamic_creative_name" class="creative-name">
                                        <el-tag size="small" type="warning">创意</el-tag>
                                        {{ row.dynamic_creative_name }}
                                    </div>
                                    <div v-if="!row.adgroup_name && !row.dynamic_creative_name" class="random-info">
                                        <el-tag size="small" type="info">随机投放</el-tag>
                                    </div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="号码分类" width="120">
                            <template #default="{ row }">
                                <span v-if="row.category_name">{{ row.category_name }}</span>
                                <span v-else class="empty-value">全部号码</span>
                            </template>
                        </el-table-column>

                        <el-table-column label="投放配置" width="150">
                            <template #default="{ row }">
                                <div class="delivery-config">
                                    <div>每次: {{ row.wechat_count_per_delivery }} 个</div>
                                    <div>时段: {{ row.time_periods?.length || 0 }} 个</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="任务状态" width="100">
                            <template #default="{ row }">
                                <el-tag :type="row.status ? 'success' : 'info'" effect="light">
                                    {{ row.status ? '运行中' : '已停用' }}
                                </el-tag>
                                <div v-if="row.next_delivery_time" class="next-time">
                                    {{ formatTime(row.next_delivery_time) }}
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="投放统计" width="120">
                            <template #default="{ row }">
                                <div class="delivery-stats">
                                    <div>成功: {{ row.success_delivery_count || 0 }}</div>
                                    <div>总计: {{ row.total_delivery_count || 0 }}</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="最后投放" width="180">
                            <template #default="{ row }">
                                <span v-if="row.last_delivery_time">{{ formatTime(row.last_delivery_time) }}</span>
                                <span v-else class="empty-value">暂无投放</span>
                            </template>
                        </el-table-column>

                        <el-table-column label="操作" width="350" fixed="right">
                            <template #default="{ row }">
                                <div class="action-buttons">
                                    <el-button type="primary" size="small" @click="editTask(row)">
                                        <el-icon>
                                            <Edit />
                                        </el-icon>
                                        编辑
                                    </el-button>
                                    <el-button :type="row.status ? 'warning' : 'success'" size="small"
                                        @click="toggleTaskStatus(row)">
                                        <el-icon v-if="row.status">
                                            <VideoPause />
                                        </el-icon>
                                        <el-icon v-else>
                                            <CircleCheck />
                                        </el-icon>
                                        {{ row.status ? '暂停' : '启用' }}
                                    </el-button>
                                    <el-button type="danger" size="small" @click="deleteTask(row.id)">
                                        <el-icon>
                                            <Delete />
                                        </el-icon>
                                        删除
                                    </el-button>
                                    <el-button type="info" size="small" @click="manualDelivery(row)">
                                        <el-icon>
                                            <Promotion />
                                        </el-icon>
                                        立即投放
                                    </el-button>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>

                    <!-- 分页 -->
                    <div class="pagination-container">
                        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                            :total="pagination.total" :page-sizes="[10, 20, 50, 100]"
                            layout="total, sizes, prev, pager, next, jumper" @size-change="fetchTaskList"
                            @current-change="fetchTaskList" />
                    </div>
                </div>
            </div>
        </div>

        <!-- 创建/编辑任务弹窗 -->
        <el-dialog :title="editMode ? '编辑定时任务' : '新建定时任务'" v-model="showCreateDialog" width="900px"
            :close-on-click-modal="false">

            <el-form :model="taskForm" label-width="140px" ref="taskFormRef">
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="任务名称" prop="task_name" required>
                            <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="任务状态">
                            <el-switch v-model="taskForm.status" active-text="启用" inactive-text="停用" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-form-item label="选择广告账户" prop="gzh_id" required>
                    <el-select v-model="taskForm.gzh_id" placeholder="请选择广告账户" style="width: 100%"
                        @change="handleGzhChange" clearable>
                        <el-option v-for="gzh in gzhList" :key="gzh.id" :label="gzh.ghz_name" :value="gzh.id" />
                    </el-select>
                </el-form-item>

                <el-form-item label="选择广告商" prop="account_id" required>
                    <el-select v-model="taskForm.account_id" placeholder="请先选择广告账户" style="width: 100%"
                        @change="handleAccountChange" :disabled="!taskForm.gzh_id" clearable>
                        <el-option v-for="account in accountList" :key="account.account_id"
                            :label="`${account.corporation_name} (${account.account_id})`"
                            :value="account.account_id" />
                    </el-select>
                </el-form-item>

                <el-form-item label="选择广告" prop="adgroup_id">
                    <el-select v-model="taskForm.adgroup_id" placeholder="请选择广告（可选）" style="width: 100%"
                        @change="handleAdChange" :disabled="!taskForm.account_id" clearable>
                        <el-option label="广告预览（不选择具体广告）" value="" />
                        <el-option v-for="ad in adList" :key="ad.adgroup_id" :label="ad.adgroup_name"
                            :value="ad.adgroup_id" />
                    </el-select>
                    <div class="form-tip">不选择广告将进行账户级别的广告预览</div>
                </el-form-item>

                <el-form-item label="选择创意">
                    <el-select v-model="taskForm.dynamic_creative_id" placeholder="请选择创意（可选）" style="width: 100%"
                        :disabled="!taskForm.adgroup_id" clearable>
                        <el-option label="随机选择创意" value="" />
                        <el-option v-for="creative in creativeList" :key="creative.dynamic_creative_id"
                            :label="creative.dynamic_creative_name" :value="creative.dynamic_creative_id" />
                    </el-select>
                    <div class="form-tip">不选择创意将随机选择该广告下的一个创意</div>
                </el-form-item>

                <el-form-item label="号码分类">
                    <el-select v-model="taskForm.category_id" placeholder="请选择号码分类（可选）" style="width: 100%" clearable>
                        <el-option label="全部号码" value="" />
                        <el-option v-for="category in categoryList" :key="category.id" :label="category.name"
                            :value="category.id" />
                    </el-select>
                    <div class="form-tip">不选择分类将使用所有可用的微信号</div>
                </el-form-item>

                <el-form-item label="每次投放数量" prop="wechat_count_per_delivery" required>
                    <el-input-number v-model="taskForm.wechat_count_per_delivery" :min="1" :max="50"
                        controls-position="right" />
                    <div class="form-tip">每次投放使用的微信号数量，最多50个</div>
                </el-form-item>

                <el-form-item label="投放时间段" prop="time_periods" required>
                    <div class="time-periods-container">
                        <div class="time-periods-header">
                            <span>时间段配置</span>
                            <el-button type="primary" text @click="addTimePeriod">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                                添加时间段
                            </el-button>
                        </div>

                        <div class="time-periods-list">
                            <div v-for="(period, index) in taskForm.time_periods" :key="index" class="period-item">
                                <div class="period-time">
                                    <el-time-picker v-model="period.start_time" placeholder="开始时间" format="HH:mm"
                                        value-format="HH:mm" style="width: 120px" />
                                    <span class="time-separator">至</span>
                                    <el-time-picker v-model="period.end_time" placeholder="结束时间" format="HH:mm"
                                        value-format="HH:mm" style="width: 120px" />
                                </div>
                                <div class="period-count">
                                    <el-input-number v-model="period.max_deliveries" :min="1" :max="100"
                                        placeholder="最大次数" controls-position="right" />
                                    <span class="count-label">次/时段</span>
                                </div>
                                <el-button text @click="removeTimePeriod(index)"
                                    :disabled="taskForm.time_periods.length === 1">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                </el-button>
                            </div>
                        </div>

                        <div class="form-tip">系统将在设定的时间段内自动投放，每个时段不超过设定的最大次数</div>
                    </div>
                </el-form-item>

                <el-form-item label="备注">
                    <el-input v-model="taskForm.notes" type="textarea" :rows="3" placeholder="请输入任务备注信息" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="showCreateDialog = false">取消</el-button>
                <el-button type="primary" @click="submitTask" :loading="submitting">
                    {{ editMode ? '更新任务' : '创建任务' }}
                </el-button>
            </template>
        </el-dialog>

        <!-- 投放日志弹窗 -->
        <el-dialog title="投放日志" v-model="showLogsDialog" width="1050px" top="5vh" @open="fetchDeliveryLogs">
            <div class="logs-content">
                <div class="logs-filter">
                    <el-form :inline="true" :model="logsFilter">
                        <el-form-item label="任务">
                            <el-select v-model="logsFilter.task_id" placeholder="选择任务" clearable style="width:100px">
                                <el-option v-for="task in taskList" :key="task.id" :label="task.task_name"
                                    :value="task.id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="投放类型">
                            <el-select v-model="logsFilter.delivery_type" placeholder="选择类型" clearable
                                style="width:100px">
                                <el-option label="广告预览" value="ad_preview" />
                                <el-option label="创意预览" value="creative_preview" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="日期">
                            <el-date-picker v-model="logsFilter.date_range" type="daterange" range-separator="至"
                                start-placeholder="开始日期" end-placeholder="结束日期" value-format="YYYY-MM-DD" />
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" @click="fetchDeliveryLogs">搜索</el-button>
                            <el-button @click="resetLogsFilter">重置</el-button>
                        </el-form-item>
                    </el-form>
                </div>

                <el-table :data="deliveryLogs" v-loading="logsLoading" height="500">
                    <el-table-column prop="task_name" label="任务名称" width="150" />
                    <el-table-column label="投放类型" width="150">
                        <template #default="{ row }">
                            <el-tag :type="row.delivery_type === 'ad_preview' ? 'primary' : 'warning'">
                                {{ row.delivery_type === 'ad_preview' ? '广告预览' : '创意预览' }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="投放目标" width="200" show-overflow-tooltip>
                        <template #default="{ row }">
                            <div v-if="row.adgroup_name">{{ row.adgroup_name }}</div>
                            <div v-if="row.dynamic_creative_name">{{ row.dynamic_creative_name }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column label="微信号" width="120">
                        <template #default="{ row }">
                            {{ row.delivery_count }} 个
                        </template>
                    </el-table-column>
                    <el-table-column label="投放结果" width="120">
                        <template #default="{ row }">
                            <el-tag :type="row.fail_count > 0 ? 'warning' : 'success'">
                                成功: {{ row.success_count }} 失败: {{ row.fail_count }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="delivery_time" label="投放时间" width="180">
                        <template #default="{ row }">
                            {{ formatTime(row.delivery_time) }}
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" width="100">
                        <template #default="{ row }">
                            <el-button type="primary" text @click="viewLogDetail(row)">详情</el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="pagination-container">
                    <el-pagination v-model:current-page="logsPagination.page"
                        v-model:page-size="logsPagination.pageSize" :total="logsPagination.total"
                        :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next"
                        @size-change="fetchDeliveryLogs" @current-change="fetchDeliveryLogs" />
                </div>
            </div>
        </el-dialog>

        <!-- 日志详情弹窗 -->
        <el-dialog title="投放详情" v-model="showLogDetailDialog" width="600px">
            <div v-if="currentLogDetail" class="log-detail">
                <el-descriptions :column="1" border>
                    <el-descriptions-item label="任务名称">{{ currentLogDetail.task_name }}</el-descriptions-item>
                    <el-descriptions-item label="投放类型">
                        <el-tag :type="currentLogDetail.delivery_type === 'ad_preview' ? 'primary' : 'warning'">
                            {{ currentLogDetail.delivery_type === 'ad_preview' ? '广告预览' : '创意预览' }}
                        </el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="投放时间">{{ formatTime(currentLogDetail.delivery_time)
                    }}</el-descriptions-item>
                    <el-descriptions-item label="投放微信号">
                        <div class="wechat-list">
                            <el-tag v-for="(wechat, index) in currentLogDetail.wechat_accounts" :key="index"
                                size="small" class="wechat-tag">
                                {{ wechat }}
                            </el-tag>
                        </div>
                    </el-descriptions-item>
                    <el-descriptions-item label="投放结果">
                        成功: {{ currentLogDetail.success_count }} 个,
                        失败: {{ currentLogDetail.fail_count }} 个
                    </el-descriptions-item>
                    <el-descriptions-item label="错误信息" v-if="currentLogDetail.error_message">
                        <el-alert :title="currentLogDetail.error_message" type="error" :closable="false" />
                    </el-descriptions-item>
                </el-descriptions>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Delete, Refresh, Document,
    CircleCheck, Clock,
    VideoPause, Promotion, Plus,
    Edit, TrendCharts
} from '@element-plus/icons-vue'

export default {
    name: 'ScheduledDelivery',
    components: {
        Delete, Refresh, Document,
        CircleCheck, Clock,
        VideoPause, Promotion, Plus,
        Edit, TrendCharts
    },
    data() {
        return {
            loading: false,
            submitting: false,
            logsLoading: false,

            // 任务列表数据
            taskList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 统计信息
            stats: {},

            // 弹窗控制
            showCreateDialog: false,
            showLogsDialog: false,
            showLogDetailDialog: false,
            editMode: false,

            // 表单数据
            taskForm: {
                task_name: '',
                gzh_id: '',
                account_id: '',
                adgroup_id: '',
                dynamic_creative_id: '',
                category_id: '',
                wechat_count_per_delivery: 5,
                time_periods: [
                    { start_time: '09:00', end_time: '12:00', max_deliveries: 3 },
                    { start_time: '14:00', end_time: '18:00', max_deliveries: 3 }
                ],
                status: true,
                notes: ''
            },

            // 选项数据
            gzhList: [],
            accountList: [],
            adList: [],
            creativeList: [],
            categoryList: [],

            // 日志相关
            deliveryLogs: [],
            currentLogDetail: null,
            logsFilter: {
                task_id: '',
                delivery_type: '',
                date_range: []
            },
            logsPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    mounted() {
        this.fetchTaskList();
        this.fetchStats();
        this.fetchGzhList();
        this.fetchCategoryList();
    },
    methods: {
        // 获取任务列表
        async fetchTaskList() {
            this.loading = true;
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_delivery.php`, {
                    action: 'get_tasks',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                });

                if (response.data.data) {
                    this.taskList = response.data.data;
                    this.pagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                this.$message.error('获取任务列表失败');
                console.error('获取任务列表错误:', error);
            } finally {
                this.loading = false;
            }
        },

        // 获取统计信息
        async fetchStats() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_delivery.php`, {
                    action: 'get_task_stats',
                    store_id: this.userInfo.id
                });

                if (response.data) {
                    this.stats = response.data;
                }
            } catch (error) {
                console.error('获取统计信息失败:', error);
            }
        },

        // 获取广告账户列表
        async fetchGzhList() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, {
                    action: 'list_all',
                    store_id: this.userInfo.id
                });

                if (response.data.data) {
                    this.gzhList = response.data.data.filter(item => item.ghz_appid && item.ghz_appid.trim() !== '');
                }
            } catch (error) {
                console.error('获取广告账户列表失败:', error);
            }
        },

        // 获取号码分类列表
        async fetchCategoryList() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/number_management_category.php`, {
                    action: 'list',
                    store_id: this.userInfo.id,
                    page: 1,
                    page_size: 1000 // 获取所有分类
                });

                if (response.data.data) {
                    this.categoryList = response.data.data;
                }
            } catch (error) {
                console.error('获取号码分类失败:', error);
                this.$message.error('获取号码分类失败');
            }
        },

        // 广告账户选择变化
        async handleGzhChange() {
            if (this.taskForm.gzh_id) {
                // 清空所有下级选项
                this.accountList = [];
                this.adList = [];
                this.creativeList = [];
                this.taskForm.account_id = '';
                this.taskForm.adgroup_id = '';
                this.taskForm.dynamic_creative_id = '';

                // 重新加载该广告账户下的广告商
                await this.fetchAccountsByGzh(this.taskForm.gzh_id);
            } else {
                // 清空所有选项
                this.accountList = [];
                this.adList = [];
                this.creativeList = [];
                this.taskForm.account_id = '';
                this.taskForm.adgroup_id = '';
                this.taskForm.dynamic_creative_id = '';
            }
        },

        // 根据广告账户获取广告商 - 修改
        async fetchAccountsByGzh(gzhId) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/advertiser_sync.php`, {
                    action: 'list',
                    store_id: this.userInfo.id
                });

                if (response.data.data) {
                    // 过滤出当前广告账户下的广告商
                    this.accountList = response.data.data.filter(account =>
                        account.auth_id == gzhId  // 确保只显示当前授权账户的广告商
                    );
                }
            } catch (error) {
                console.error('获取广告商失败:', error);
            }
        },

        // 广告商选择变化
        async handleAccountChange(accountId) {
            if (accountId) {
                // 清空下级选项
                this.adList = [];
                this.creativeList = [];
                this.taskForm.adgroup_id = '';
                this.taskForm.dynamic_creative_id = '';

                // 重新加载该账户下的广告
                await this.fetchAdsByAccount(accountId);
            } else {
                // 清空下级选项
                this.adList = [];
                this.creativeList = [];
                this.taskForm.adgroup_id = '';
                this.taskForm.dynamic_creative_id = '';
            }
        },

        // 根据账户获取广告 - 修改
        async fetchAdsByAccount(accountId) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/ad_sync.php`, {
                    action: 'list_ads',
                    store_id: this.userInfo.id,
                    account_id: accountId  // 确保只获取该账户的广告
                });

                if (response.data.data) {
                    this.adList = response.data.data;
                }
            } catch (error) {
                console.error('获取广告列表失败:', error);
            }
        },

        // 广告选择变化
        async handleAdChange(adgroupId) {
            if (adgroupId) {
                // 清空下级选项
                this.creativeList = [];
                this.taskForm.dynamic_creative_id = '';

                // 重新加载该广告下的创意
                await this.fetchCreativesByAd(adgroupId);
            } else {
                // 清空下级选项
                this.creativeList = [];
                this.taskForm.dynamic_creative_id = '';
            }
        },

        // 根据广告获取创意 - 修改
        async fetchCreativesByAd(adgroupId) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/creative_sync.php`, {
                    action: 'list_creatives',
                    store_id: this.userInfo.id,
                    adgroup_id: adgroupId,  // 确保只获取该广告的创意
                    account_id: this.taskForm.account_id  // 添加账户ID过滤
                });

                if (response.data.data) {
                    this.creativeList = response.data.data;
                }
            } catch (error) {
                console.error('获取创意列表失败:', error);
            }
        },

        // 添加时间段
        addTimePeriod() {
            this.taskForm.time_periods.push({
                start_time: '09:00',
                end_time: '12:00',
                max_deliveries: 3
            });
        },

        // 删除时间段
        removeTimePeriod(index) {
            if (this.taskForm.time_periods.length > 1) {
                this.taskForm.time_periods.splice(index, 1);
            } else {
                this.$message.warning('至少需要保留一个时间段');
            }
        },

        // 提交任务
        async submitTask() {
            if (!this.taskForm.task_name.trim()) {
                this.$message.warning('请输入任务名称');
                return;
            }

            if (!this.taskForm.gzh_id) {
                this.$message.warning('请选择广告账户');
                return;
            }

            if (!this.taskForm.account_id) {
                this.$message.warning('请选择广告商');
                return;
            }

            if (this.taskForm.time_periods.length === 0) {
                this.$message.warning('请至少设置一个投放时间段');
                return;
            }

            this.submitting = true;
            try {
                const formData = {
                    ...this.taskForm,
                    store_id: this.userInfo.id,
                    status: this.taskForm.status ? 1 : 0,

                };

                const action = this.editMode ? 'update_task' : 'create_task';
                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_delivery.php`, {
                    action: action,
                    ...formData
                });

                if (response.data.success) {
                    this.$message.success(this.editMode ? '任务更新成功' : '任务创建成功');
                    this.showCreateDialog = false;
                    this.fetchTaskList();
                    this.fetchStats();
                    this.resetTaskForm();
                } else {
                    this.$message.error(response.data.error || '操作失败');
                }
            } catch (error) {
                this.$message.error('操作失败');
                console.error('提交任务错误:', error);
            } finally {
                this.submitting = false;
            }
        },

        // 编辑任务 - 修改数据加载逻辑
        async editTask(task) {
            this.editMode = true;
            this.taskForm = {
                ...task,
                status: task.status === 1
            };

            // 处理时间格式
            if (typeof this.taskForm.time_periods === 'string') {
                this.taskForm.time_periods = JSON.parse(this.taskForm.time_periods);
            }

            // 重新加载相关选项数据（按正确的数据链）
            try {
                // 加载广告账户数据
                await this.fetchGzhList();

                // 如果任务有广告账户ID，加载对应的广告商
                if (this.taskForm.gzh_id) {
                    await this.fetchAccountsByGzh(this.taskForm.gzh_id);

                    // 等待数据加载完成后再继续
                    await this.$nextTick();

                    // 如果任务有广告商ID，加载对应的广告
                    if (this.taskForm.account_id) {
                        await this.fetchAdsByAccount(this.taskForm.account_id);

                        // 等待数据加载完成后再继续
                        await this.$nextTick();

                        // 如果任务有广告ID，加载对应的创意
                        if (this.taskForm.adgroup_id) {
                            await this.fetchCreativesByAd(this.taskForm.adgroup_id);
                        }
                    }
                }
            } catch (error) {
                console.error('编辑任务时加载选项数据失败:', error);
            }

            this.showCreateDialog = true;
        },

        // 重置表单
        resetTaskForm() {
            this.taskForm = {
                task_name: '',
                gzh_id: '',
                account_id: '',
                adgroup_id: '',
                dynamic_creative_id: '',
                category_id: '',
                wechat_count_per_delivery: 5,
                time_periods: [
                    { start_time: '09:00', end_time: '12:00', max_deliveries: 3 },
                    { start_time: '14:00', end_time: '18:00', max_deliveries: 3 }
                ],
                status: true,
                notes: ''
            };
            this.editMode = false;
        },

        // 切换任务状态
        async toggleTaskStatus(task) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_delivery.php`, {
                    action: 'toggle_task_status',
                    store_id: this.userInfo.id,
                    task_id: task.id,
                    status: task.status ? 0 : 1
                });

                if (response.data.success) {
                    this.$message.success(task.status ? '任务已暂停' : '任务已启用');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '操作失败');
                }
            } catch (error) {
                this.$message.error('操作失败');
                console.error('切换任务状态错误:', error);
            }
        },

        // 删除任务
        async deleteTask(taskId) {
            try {
                await this.$confirm('确定要删除这个定时任务吗？', '确认删除', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_delivery.php`, {
                    action: 'delete_task',
                    store_id: this.userInfo.id,
                    task_id: taskId
                });

                if (response.data.success) {
                    this.$message.success('任务删除成功');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '删除失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('删除失败');
                    console.error('删除任务错误:', error);
                }
            }
        },

        // 立即投放
        async manualDelivery(task) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_delivery.php`, {
                    action: 'manual_delivery',
                    store_id: this.userInfo.id,
                    task_id: task.id
                });

                if (response.data.success) {
                    this.$message.success('投放完成');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '投放失败');
                }
            } catch (error) {
                this.$message.error('投放失败');
                console.error('立即投放错误:', error);
            }
        },

        // 获取投放日志
        async fetchDeliveryLogs() {
            this.logsLoading = true;
            try {
                const params = {
                    action: 'get_delivery_logs',
                    store_id: this.userInfo.id,
                    page: this.logsPagination.page,
                    page_size: this.logsPagination.pageSize
                };

                if (this.logsFilter.task_id) {
                    params.task_id = this.logsFilter.task_id;
                }
                if (this.logsFilter.delivery_type) {
                    params.delivery_type = this.logsFilter.delivery_type;
                }
                if (this.logsFilter.date_range && this.logsFilter.date_range.length === 2) {
                    params.start_date = this.logsFilter.date_range[0];
                    params.end_date = this.logsFilter.date_range[1];
                }

                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_delivery.php`, params);

                if (response.data.data) {
                    this.deliveryLogs = response.data.data;
                    this.logsPagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                this.$message.error('获取投放日志失败');
                console.error('获取投放日志错误:', error);
            } finally {
                this.logsLoading = false;
            }
        },

        // 重置日志筛选
        resetLogsFilter() {
            this.logsFilter = {
                task_id: '',
                delivery_type: '',
                date_range: []
            };
            this.fetchDeliveryLogs();
        },

        // 查看日志详情
        viewLogDetail(log) {
            this.currentLogDetail = log;
            this.showLogDetailDialog = true;
        },

        // 工具函数
        formatTime(time) {
            if (!time) return '-';
            return new Date(time).toLocaleString('zh-CN');
        }
    }
}
</script>

<style scoped lang="scss">
.scheduled-delivery-page {
    padding: 20px;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.header-section {
    margin-bottom: 24px;
}

.stats-section {
    margin-bottom: 24px;
}

.stat-card {
    display: flex;
    align-items: center;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

    .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
        font-size: 24px;
        color: white;

        &.total {
            background: linear-gradient(135deg, #409EFF, #67C2EF);
        }

        &.active {
            background: linear-gradient(135deg, #67C23A, #85CE61);
        }

        &.success {
            background: linear-gradient(135deg, #E6A23C, #EBB563);
        }

        &.today {
            background: linear-gradient(135deg, #909399, #A6A9AD);
        }
    }

    .stat-content {
        flex: 1;

        .stat-number {
            font-size: 24px;
            font-weight: 700;
            color: #303133;
            margin-bottom: 4px;
        }

        .stat-label {
            font-size: 14px;
            color: #909399;
        }
    }
}

.action-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.task-list-section {
    margin-top: 20px;
}

.target-info {

    .ad-name,
    .creative-name {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 4px;
    }
}

.delivery-config,
.delivery-stats {
    font-size: 12px;
    color: #606266;
    line-height: 1.4;
}

.next-time {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

.empty-value {
    color: #909399;
    font-style: italic;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* 时间段配置样式 */
.time-periods-container {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 16px;
    background: #fafafa;
}

.time-periods-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    font-weight: 500;
    color: #606266;
}

.time-periods-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.period-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: white;
    border-radius: 4px;
    border: 1px solid #e4e7ed;
}

.period-time {
    display: flex;
    align-items: center;
    gap: 8px;
}

.time-separator {
    color: #909399;
    font-size: 14px;
}

.period-count {
    display: flex;
    align-items: center;
    gap: 8px;
}

.count-label {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

/* 日志相关样式 */
.logs-content {
    .logs-filter {
        margin-bottom: 16px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 6px;
    }
}

.wechat-list {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-height: 80px;
    overflow-y: auto;
}

.wechat-tag {
    margin: 2px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
    .settings-form {
        max-width: 100%;
        padding: 20px;
    }

    .period-item {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .period-time,
    .period-count {
        justify-content: center;
    }
}

/* 表格样式优化 */
.el-table {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table__header) th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
    }

    :deep(.el-table__body) tr:hover>td {
        background-color: #f0f7ff !important;
    }
}

.action-buttons {
    display: flex;
    gap: 8px; // 统一间距为8px

    .el-button {
        flex: 1; // 让所有按钮等宽
        min-width: 0; // 允许缩小
        display: inline-flex;
        align-items: center;
        justify-content: center;

        .el-icon {
            margin-right: 4px;
        }
    }
}
</style>